// ============================================================================
// WorkbenchOutputs.jsx
// Deliverables tracking — with "Go to" links to Advisor tabs and
// detection of whether content has been generated.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Check,
  ExternalLink,
  FileText,
} from 'lucide-react';
import {
  DELIVERABLES, DELIVERABLE_STATUS, DELIVERABLE_DESCRIPTIONS, DELIVERABLE_TAB_MAP,
  getModelID,
} from '@process-advisor/process-miner/consultant/workbenchShared';
import { getCachedConsultantContent } from '@process-advisor/process-miner/consultant/consultantContentState';
import * as S from '@process-advisor/process-miner/consultant/workbenchStyles';

// ── Navigation helper ───────────────────────────────────────────────────────
const navigateToTab = (tabId) => {
  if (!tabId || typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('metricus:consultant-tab-change', {
    detail: { tabId },
  }));
};

// ── Status label colour mapping ─────────────────────────────────────────────
const statusBorderColor = (status) => {
  switch (status) {
    case 'final':    return S.C.G400;
    case 'reviewed': return S.C.B400;
    case 'draft':    return S.C.Y400;
    default:         return S.C.N40;
  }
};

// ── Component ───────────────────────────────────────────────────────────────

const WorkbenchOutputs = ({ workspace = {}, onUpdate }) => {
  const deliverables = workspace.deliverables || {};
  const modelId = getModelID();

  // Detect which deliverables have generated content in session cache
  const [generated, setGenerated] = useState({});
  useEffect(() => {
    const g = {};
    DELIVERABLES.forEach(([key, , column]) => {
      if (!column) return;
      const cached = getCachedConsultantContent({ modelId, column });
      if (cached) g[key] = true;
    });
    setGenerated(g);
  }, [modelId]);

  const completedCount = Object.values(deliverables).filter(v => v === 'reviewed' || v === 'final').length;

  return (
    <div style={S.ROOT}>
      <div style={S.SHELL}>
        <div style={S.CARD}>

          <div style={S.CARD_HEADER}>
            <div style={S.CARD_TITLE_ROW}>
              <FileText size={17} color={S.C.P500} />
              <div style={S.CARD_TITLE}>Assessment Readiness</div>
              <span style={S.PILL}>{completedCount}/{DELIVERABLES.length}</span>
            </div>
          </div>

          <div style={S.CARD_BODY}>
            <div style={{ ...S.DIVIDER_NOTE, marginBottom: 14 }}>
              Track which assessment components are ready before generating the final report. Use "Go to" to navigate to the relevant analysis area.
            </div>

            <div style={S.OUTPUTS_GRID}>
              {DELIVERABLES.map(([key, label, column]) => {
                const status  = deliverables[key] || 'not_started';
                const tabId   = DELIVERABLE_TAB_MAP[key];
                const hasContent = !!generated[key];
                const border  = statusBorderColor(status);

                return (
                  <div
                    key={key}
                    style={{
                      ...S.OUTPUT_CARD,
                      borderLeftWidth: 4,
                      borderLeftColor: border,
                    }}
                  >
                    <div style={S.OUTPUT_TOP}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={S.OUTPUT_TITLE}>{label}</div>
                          {hasContent && (
                            <span style={S.statusPillStyle('green')}>
                              <Check size={12} style={{ marginRight: 3 }} /> Generated
                            </span>
                          )}
                        </div>
                        <div style={S.OUTPUT_DESC}>{DELIVERABLE_DESCRIPTIONS[key]}</div>
                      </div>
                      <select
                        style={{ ...S.SELECT, width: 150, flex: '0 0 150px' }}
                        value={status}
                        onChange={(e) => onUpdate({ deliverables: { ...deliverables, [key]: e.target.value } })}
                      >
                        {DELIVERABLE_STATUS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                      </select>
                    </div>

                    {tabId && (
                      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          style={{ ...S.BUTTON_SECONDARY, fontSize: 11, height: 28, padding: '0 10px' }}
                          onClick={() => navigateToTab(tabId)}
                        >
                          Go to {label.split(' ')[0]} <ExternalLink size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkbenchOutputs;
